import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Loader2 } from "lucide-react";

interface OrderRatingSheetProps {
  restaurantName: string;
  restaurantLogo?: string | null;
  orderDate: string;
  orderItems: { name: string; quantity: number }[];
  primaryColor: string;
  onSubmit: (data: {
    foodRating: number;
    serviceRating: number;
    deliveryRating: number;
    tags: string[];
    comment: string;
    npsScore: number | null;
  }) => Promise<void>;
  onSkip: () => void;
  showDeliveryRating?: boolean;
}

const FEEDBACK_TAGS = [
  "Comida Saborosa", "Bem temperada", "Boa aparência", "Boa quantidade",
  "Boa embalagem", "Temperatura certa", "Bons ingredientes",
  "No ponto certo", "Embalagem sustentável",
];

const RATING_LABELS = ["", "Péssimo", "Ruim", "Regular", "Bom", "Excelente"];

export const OrderRatingSheet = ({
  restaurantName, restaurantLogo, orderDate, orderItems,
  primaryColor, onSubmit, onSkip, showDeliveryRating = true,
}: OrderRatingSheetProps) => {
  const [step, setStep] = useState<0 | 1>(0); // 0 = rating, 1 = tags+comment
  const [foodRating, setFoodRating] = useState(0);
  const [serviceRating, setServiceRating] = useState(0);
  const [deliveryRating, setDeliveryRating] = useState(0);
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
  const [comment, setComment] = useState("");
  const [npsScore, setNpsScore] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const overallRating = Math.round((foodRating + serviceRating + (showDeliveryRating ? deliveryRating : serviceRating)) / (showDeliveryRating ? 3 : 2));

  const toggleTag = (tag: string) => {
    setSelectedTags(prev => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag); else next.add(tag);
      return next;
    });
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSubmit({
        foodRating, serviceRating, deliveryRating: showDeliveryRating ? deliveryRating : serviceRating,
        tags: Array.from(selectedTags), comment: comment.trim(), npsScore,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const canProceed = foodRating > 0 && serviceRating > 0 && (!showDeliveryRating || deliveryRating > 0);

  return (
    <motion.div
      initial={{ y: "100%" }}
      animate={{ y: 0 }}
      exit={{ y: "100%" }}
      transition={{ type: "spring", damping: 28, stiffness: 300 }}
      className="bg-white rounded-t-[24px] max-h-[90vh] flex flex-col shadow-[0_-8px_40px_rgba(0,0,0,0.15)]"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Handle + header */}
      <div className="flex justify-center pt-3 pb-1">
        <div className="w-10 h-1.5 rounded-full bg-[hsl(0,0%,85%)]" />
      </div>
      <div className="text-center px-5 pb-3 border-b border-[hsl(0,0%,95%)]">
        <p className="text-xs font-bold text-[hsl(0,0%,30%)] uppercase tracking-wider">Avaliação do Pedido</p>
        {/* Step indicator */}
        <div className="flex gap-1.5 justify-center mt-2">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: step === 0 ? primaryColor : `${primaryColor}30` }} />
          <div className="w-3 h-2 rounded-full" style={{ backgroundColor: step === 1 ? primaryColor : `${primaryColor}30` }} />
        </div>
      </div>

      <div className="flex-1 overflow-auto px-5 py-5">
        <AnimatePresence mode="wait">
          {step === 0 ? (
            <motion.div key="step0" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6">
              {/* Restaurant info */}
              <div className="flex items-center gap-3">
                {restaurantLogo ? (
                  <img src={restaurantLogo} alt="" className="w-12 h-12 rounded-xl object-cover" />
                ) : (
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center text-lg font-bold text-white" style={{ backgroundColor: primaryColor }}>
                    {restaurantName.charAt(0)}
                  </div>
                )}
                <div>
                  <p className="font-bold text-sm text-[hsl(0,0%,13%)]">{restaurantName}</p>
                  <p className="text-xs text-[hsl(0,0%,55%)]">{orderDate}</p>
                </div>
              </div>

              {/* Order items preview */}
              {orderItems.length > 0 && (
                <div className="text-sm text-[hsl(0,0%,35%)]">
                  {orderItems.slice(0, 3).map((item, i) => (
                    <p key={i}>{item.quantity} {item.name}</p>
                  ))}
                  {orderItems.length > 3 && <p className="text-[hsl(0,0%,55%)]">e mais {orderItems.length - 3}...</p>}
                </div>
              )}

              {/* Main rating */}
              <div className="text-center space-y-3">
                <h3 className="text-lg font-bold text-[hsl(0,0%,10%)]">O que você achou do pedido? <span className="text-[hsl(0,80%,50%)]">*</span></h3>
                <p className="text-xs text-[hsl(0,0%,55%)]">Escolha de 1 a 5 estrelas para classificar.</p>
                <div className="flex gap-2 justify-center">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <motion.button
                      key={star}
                      onClick={() => setFoodRating(star)}
                      whileTap={{ scale: 0.85 }}
                      whileHover={{ scale: 1.1 }}
                    >
                      <Star className={`h-11 w-11 transition-all ${star <= foodRating ? "fill-amber-400 text-amber-400" : "text-gray-200"}`} />
                    </motion.button>
                  ))}
                </div>
                {foodRating > 0 && (
                  <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-sm font-semibold text-[hsl(0,0%,30%)]">
                    {RATING_LABELS[foodRating]}
                  </motion.p>
                )}
              </div>

              {/* Service rating */}
              <div className="text-center space-y-2">
                <p className="text-sm font-semibold text-[hsl(0,0%,20%)]">🏪 Atendimento</p>
                <div className="flex gap-1.5 justify-center">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <motion.button key={star} onClick={() => setServiceRating(star)} whileTap={{ scale: 0.85 }}>
                      <Star className={`h-8 w-8 transition-all ${star <= serviceRating ? "fill-amber-400 text-amber-400" : "text-gray-200"}`} />
                    </motion.button>
                  ))}
                </div>
              </div>

              {/* Delivery rating */}
              {showDeliveryRating && (
                <div className="text-center space-y-2">
                  <p className="text-sm font-semibold text-[hsl(0,0%,20%)]">🛵 Entrega</p>
                  <div className="flex gap-1.5 justify-center">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <motion.button key={star} onClick={() => setDeliveryRating(star)} whileTap={{ scale: 0.85 }}>
                        <Star className={`h-8 w-8 transition-all ${star <= deliveryRating ? "fill-amber-400 text-amber-400" : "text-gray-200"}`} />
                      </motion.button>
                    ))}
                  </div>
                </div>
              )}

              {canProceed && (
                <motion.button
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  onClick={() => setStep(1)}
                  className="w-full h-[48px] rounded-2xl font-bold text-[14px] text-white transition-all active:scale-[0.97]"
                  style={{ backgroundColor: primaryColor }}
                >
                  Próximo
                </motion.button>
              )}
            </motion.div>
          ) : (
            <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} className="space-y-6">
              {/* Feedback tags */}
              <div className="space-y-3">
                <h3 className="text-base font-bold text-[hsl(0,0%,10%)] text-center">Do que você gostou? <span className="text-[hsl(0,80%,50%)]">*</span></h3>
                <div className="flex flex-wrap gap-2 justify-center">
                  {FEEDBACK_TAGS.map((tag) => (
                    <button
                      key={tag}
                      onClick={() => toggleTag(tag)}
                      className={`px-4 py-2 rounded-full text-sm font-medium border-2 transition-all active:scale-95 ${
                        selectedTags.has(tag)
                          ? "border-transparent text-white"
                          : "border-[hsl(0,0%,88%)] text-[hsl(0,0%,30%)] bg-white"
                      }`}
                      style={selectedTags.has(tag) ? { backgroundColor: primaryColor } : {}}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>

              {/* Comment */}
              <div className="space-y-2">
                <h3 className="text-base font-bold text-[hsl(0,0%,10%)] text-center">Deixar comentário</h3>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value.slice(0, 300))}
                  placeholder="Conte-nos o que aconteceu (opcional)"
                  rows={3}
                  className="w-full border border-[hsl(0,0%,88%)] rounded-2xl p-4 text-sm resize-none focus:outline-none focus:ring-2 bg-[hsl(0,0%,98%)] focus:bg-white transition-all"
                  style={{ "--tw-ring-color": `${primaryColor}40` } as any}
                />
                <p className="text-right text-xs text-[hsl(0,0%,60%)]">{comment.length}/300</p>
                <p className="text-xs text-[hsl(0,0%,55%)]">Seu comentário vai ajudar muitas pessoas</p>
              </div>

              {/* NPS */}
              <div className="space-y-3">
                <h3 className="text-base font-bold text-[hsl(0,0%,10%)] text-center">Avalie o ViaMenu também</h3>
                <p className="text-xs text-[hsl(0,0%,55%)] text-center">Em uma escala de 0 a 10, qual é a chance de você indicar para um amigo?</p>
                <div className="flex flex-wrap gap-1.5 justify-center">
                  {Array.from({ length: 11 }, (_, i) => i).map((n) => (
                    <button
                      key={n}
                      onClick={() => setNpsScore(n)}
                      className={`w-9 h-9 rounded-lg text-sm font-bold border-2 transition-all active:scale-90 ${
                        npsScore === n
                          ? "text-white border-transparent"
                          : "border-[hsl(0,0%,88%)] text-[hsl(0,0%,35%)] bg-white"
                      }`}
                      style={npsScore === n ? { backgroundColor: primaryColor } : {}}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Bottom actions */}
      <div className="px-5 pb-5 pt-2 space-y-2 border-t border-[hsl(0,0%,95%)]">
        {step === 1 && (
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full h-[52px] rounded-2xl font-bold text-[15px] text-white disabled:opacity-40 transition-all active:scale-[0.97] shadow-lg"
            style={{ backgroundColor: primaryColor, boxShadow: `0 6px 20px ${primaryColor}50` }}
          >
            {submitting ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : "Enviar avaliação"}
          </button>
        )}
        <button onClick={onSkip} className="w-full py-2.5 text-sm text-[hsl(0,0%,55%)] hover:text-[hsl(0,0%,30%)] transition-colors">
          {step === 0 ? "Pular" : "Enviar depois"}
        </button>
      </div>
    </motion.div>
  );
};
